import { Box, styled, keyframes } from '@mui/material'

import { theme } from 'styles/theme'

const pulse = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(1, 247, 194, 0.4);
  }
  70% {
    box-shadow: 0 0 0 8px rgba(1, 247, 194, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(1, 247, 194, 0);
  }
`

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const scaleIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`

export const TriggerButton = styled('button')(() => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 36,
  height: 36,
  borderRadius: '50%',
  border: 'none',
  background: 'rgba(15, 247, 194, 0.12)',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  animation: `${pulse} 2s infinite`,
  '&:hover': {
    background: 'rgba(15, 247, 194, 0.22)',
    transform: 'scale(1.08)'
  }
}))

export const BadgeDot = styled('span')(() => ({
  position: 'absolute',
  top: 2,
  right: 2,
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: '#ff6b6b',
  border: `2px solid ${theme.palette.neutral[800]}`
}))

export const PopoverContent = styled(Box)(() => ({
  width: 370,
  maxWidth: '90vw',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: theme.palette.neutral['A200'],
  borderRadius: 16,
  overflow: 'hidden'
}))

export const HeaderBanner = styled(Box)(() => ({
  background: `linear-gradient(135deg, ${theme.palette.neutral[100]} 0%, ${theme.palette.neutral[200]} 100%)`,
  padding: '20px 24px 16px',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  position: 'relative',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 24,
    right: 24,
    height: 1,
    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}30, transparent)`
  }
}))

export const HeaderIconBox = styled(Box)(() => ({
  width: 36,
  height: 36,
  borderRadius: 10,
  background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.primary.main}08)`,
  border: `1px solid ${theme.palette.primary.main}25`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
}))

export const StepIndicator = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '0 24px',
  marginTop: 16,
  marginBottom: 4
}))

interface StepDotProps {
  active?: boolean
  completed?: boolean
}

export const StepDot = styled('span', {
  shouldForwardProp: (prop) => !['active', 'completed'].includes(prop as string)
})<StepDotProps>(({ active, completed }) => ({
  height: 3,
  borderRadius: 3,
  flex: 1,
  transition: 'all 0.3s ease',
  background: completed
    ? theme.palette.primary.main
    : active
    ? `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.primary.main}60)`
    : theme.palette.neutral[700]
}))

export const Body = styled(Box)(() => ({
  padding: '12px 24px 20px',
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  animation: `${fadeIn} 0.25s ease-out`,
  maxHeight: 320,
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: 4
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent'
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.neutral[600],
    borderRadius: 4
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: theme.palette.neutral[500]
  }
}))

export const QuestionLabel = styled('span')(() => ({
  fontWeight: 600,
  fontSize: 14,
  lineHeight: 1.4,
  color: theme.palette.neutral[200],
  fontFamily: 'Inter, Roboto, sans-serif',
  display: 'block',
  marginBottom: 4
}))

export const RadioCard = styled('label', {
  shouldForwardProp: (prop) => prop !== 'selected'
})<{ selected?: boolean }>(({ selected }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 12px',
  borderRadius: 10,
  border: `1.5px solid ${
    selected ? theme.palette.primary.main : theme.palette.neutral[700]
  }`,
  background: selected ? `${theme.palette.primary.main}08` : 'transparent',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  '&:hover': {
    borderColor: selected
      ? theme.palette.primary.main
      : theme.palette.neutral[500],
    background: selected
      ? `${theme.palette.primary.main}08`
      : theme.palette.neutral[900]
  }
}))

export const RadioCardGrid = styled(Box)(() => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 6
}))

export const RadioCardSingle = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 6
}))

export const CheckboxCard = styled('label', {
  shouldForwardProp: (prop) => prop !== 'checked'
})<{ checked?: boolean }>(({ checked }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '10px 14px',
  borderRadius: 10,
  border: `1.5px solid ${
    checked ? theme.palette.primary.main : theme.palette.neutral[700]
  }`,
  background: checked ? `${theme.palette.primary.main}08` : 'transparent',
  cursor: 'pointer',
  transition: 'all 0.15s ease',
  marginTop: 4,
  '&:hover': {
    borderColor: checked
      ? theme.palette.primary.main
      : theme.palette.neutral[500],
    background: checked
      ? `${theme.palette.primary.main}08`
      : theme.palette.neutral[900]
  }
}))

export const TextInput = styled('input')(() => ({
  border: `1.5px solid ${theme.palette.neutral[600]}`,
  borderRadius: 8,
  padding: '9px 12px',
  fontSize: 13,
  color: theme.palette.neutral[200],
  backgroundColor: theme.palette.neutral[900],
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
  fontFamily: 'Inter, Roboto, sans-serif',
  marginTop: 6,
  transition: 'border-color 0.15s ease',
  '&:focus': {
    borderColor: theme.palette.primary.main
  },
  '&::placeholder': {
    color: theme.palette.neutral[500]
  }
}))

export const ActionsRow = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '12px 24px 20px',
  borderTop: `1px solid ${theme.palette.neutral[700]}`
}))

export const SuccessContainer = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 12,
  padding: '40px 24px',
  animation: `${scaleIn} 0.35s ease-out`
}))

export const SuccessIcon = styled(Box)(() => ({
  width: 56,
  height: 56,
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.primary.main}08)`,
  border: `2px solid ${theme.palette.primary.main}30`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}))
